import { type FC } from "react";
import { Attachment } from "../../../core/types";
import FileIcon from "../../../components/icons/FileIcon";
import { useTranslation } from "../../../contexts/useTranslation";
import { Slot, Templated } from "../../../templating";

interface MessageAttachmentProps {
  attachment: Attachment;
  align?: "left" | "right";
}

const MessageAttachment: FC<MessageAttachmentProps> = ({ attachment, align = "left" }) => {
  const { translate } = useTranslation();
  const formatType = (type: string): string => {
    switch (type) {
      case "image":
        return translate("image");
      case "pdf":
        return translate("pdf_document");
      case "word":
        return translate("word_document");
      case "excel":
        return translate("excel_file");
      case "powerpoint":
        return translate("powerpoint");
      case "json":
        return translate("json_file");
      case "xml":
        return translate("xml_file");
      default:
        return translate("file");
    }
  };

  return (
    <Templated id="lastbot-attachment-chip">
      <a
        {...(attachment.url !== "#" && { href: attachment.url, target: "_blank", rel: "noopener noreferrer" })}
        className={`flex ${align === "left" ? "justify-start" : "justify-end"}`}
      >
        <div className="flex gap-x-2.5 items-center p-2.5 bg-lastbot-surface-muted rounded-xl drop-shadow-[0_1px_1px_rgba(0,0,0,0.2)]">
          <div className="shrink-0">
            <Slot name="attachment-icon"><FileIcon /></Slot>
          </div>
          <div className="text-sm min-w-32 max-w-64 font-medium">
            <div className="text-lastbot-on-surface break-words">
              <Slot name="filename">{attachment.filename}</Slot>
            </div>
            <div className="text-lastbot-on-surface-subtle pt-0.5">
              <Slot name="size">{formatType(attachment.type)} - {attachment.size}</Slot>
            </div>
          </div>
        </div>
      </a>
    </Templated>
  );
};

export default MessageAttachment;
